import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, unref as _unref, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-col gap-1 p-6 pb-3",
  "data-testid": "activity-request-drawer"
}
const _hoisted_2 = { class: "flex items-start justify-between gap-2" }
const _hoisted_3 = { class: "m-0 text-lg font-bold text-textPrimary" }
const _hoisted_4 = { class: "m-0 text-xs text-neutral50" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "m-0 text-sm" }
const _hoisted_7 = {
  key: 0,
  class: "flex flex-col gap-5 px-6 pb-6"
}
const _hoisted_8 = { "data-testid": "activity-request-strip" }
const _hoisted_9 = /*#__PURE__*/_createElementVNode("h5", { class: "mb-2 text-xs font-bold uppercase text-neutral50" }, "Previsto × efetivo × prazo", -1)
const _hoisted_10 = { class: "relative h-2 rounded bg-lightGray30" }
const _hoisted_11 = ["title"]
const _hoisted_12 = { class: "m-0 mt-2 list-none p-0 text-xs text-neutral50" }
const _hoisted_13 = /*#__PURE__*/_createElementVNode("h5", { class: "mb-2 text-xs font-bold uppercase text-neutral50" }, "Fatos", -1)
const _hoisted_14 = {
  class: "w-full text-sm",
  "data-testid": "activity-request-facts"
}
const _hoisted_15 = { class: "w-40 py-1 pr-2 text-left align-top font-normal text-neutral50" }
const _hoisted_16 = { class: "py-1" }
const _hoisted_17 = /*#__PURE__*/_createElementVNode("h5", { class: "mb-2 text-xs font-bold uppercase text-neutral50" }, "Histórico da solicitação", -1)
const _hoisted_18 = {
  key: 0,
  class: "text-sm text-neutral50"
}
const _hoisted_19 = {
  key: 1,
  class: "flex items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm",
  "data-testid": "activity-request-history-error"
}
const _hoisted_20 = {
  key: 2,
  class: "m-0 list-none p-0",
  "data-testid": "activity-request-history"
}
const _hoisted_21 = { class: "m-0 text-xs text-neutral50" }
const _hoisted_22 = { class: "m-0 text-sm font-bold" }
const _hoisted_23 = {
  key: 0,
  class: "m-0 text-xs"
}
const _hoisted_24 = {
  key: 0,
  class: "text-sm text-neutral50"
}
const _hoisted_25 = {
  class: "flex justify-end",
  "data-testid": "activity-request-drawer-send-record"
}

import { computed, ref, watch } from 'vue'

import Network from '@/Network'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import BaseSidebar from '@/components/designSystem/Sidebar/BaseSidebar.vue'
import type { QueueTone } from '@/components/designSystem/QueueViews/types'
import { REQUESTED_LEG_LABELS } from '@/constants/ds-attendance.constants'
import type { ActivityRequestItem, ActivityRequestPayload } from '@/types/activity-requests.types'
import { getApiErrorMessage } from '@/utils/apiError'
import { DEFAULT_UTC_OFFSET } from '@/utils/date'

import {
  formatBRL,
  formatDateTimeInStation,
  formatDeadlineRelative,
  formatStationPeriod,
  openingRuleLabel,
  registrationModeLabel,
  sendWindowLabel,
  timezoneLabel
} from '../../utils/activity-request-format'
import {
  buildDeadlineStrip,
  mapActivityRequestTimeline
} from '../../utils/activity-request-timeline'
import type { TimelineEntry } from '../../utils/activity-request-timeline'


export default /*#__PURE__*/_defineComponent({
  __name: 'ActivityRequestDetailDrawer',
  props: {
    modelValue: { type: Boolean },
    item: {},
    nowMs: {}
  },
  emits: ["update:modelValue", "sendRecord"],
  setup(__props: any, { emit }) {

const props = __props;





const HISTORY_BORDER: Record<QueueTone, string> = {
  neutral: 'border-lightGray30',
  info: 'border-blue-500',
  ok: 'border-green-600',
  warn: 'border-orange-500',
  crit: 'border-red-600'
}

const offset = computed(
  () => props.item?.timezone || props.item?.payload.station.timezone || DEFAULT_UTC_OFFSET
)

const strip = computed(() => {
  if (!props.item) return []
  const p = props.item.payload
  return buildDeadlineStrip({
    expectedStart: p.station.startAt,
    expectedEnd: p.station.endAt,
    effectiveIn: p.effectiveSummary?.checkin?.value ?? null,
    effectiveOut: p.effectiveSummary?.checkout?.value ?? null,
    openedAt: p.openedAt,
    deadline: p.sendDeadlineAt ?? props.item.sla?.deadlineAt ?? null,
    nowMs: props.nowMs
  })
})

const doctorFact = (item: ActivityRequestItem): string => {
  if (!item.actor) return 'Médico não informado'
  return `${item.actor.name} · ${item.actor.ourTeam ? 'Do time' : 'Fora do time'}`
}

const ruleFact = (p: ActivityRequestPayload): string => {
  const window = sendWindowLabel(p.sendWindowHours)
  const rule = openingRuleLabel(p.openingRule)
  return window ? `${rule} · janela de ${window}` : rule
}

const deadlineFact = (deadline: string | null): string => {
  if (!deadline) return 'sem prazo'
  const relative = formatDeadlineRelative(deadline, props.nowMs)
  const absolute = `${formatDateTimeInStation(deadline, offset.value)} ${timezoneLabel(offset.value)}`
  return relative ? `${absolute} — ${relative.toLowerCase()}` : absolute
}

const paymentFact = (item: ActivityRequestItem): string => {
  const kinds = [
    item.payload.payment.hasAntecipe ? 'Antecipe' : null,
    item.payload.payment.hasInCash ? 'À vista' : null
  ].filter(Boolean)
  const parts = [
    item.value ? formatBRL(item.value.amount) : null,
    kinds.length ? `${kinds.join(' + ')} retido até o registro` : null
  ].filter(Boolean)
  return parts.join(' · ') || 'sem pagamento prioritário'
}

const facts = computed(() => {
  if (!props.item) return []
  const item = props.item
  const p = item.payload
  const period = formatStationPeriod(p.station.startAt, p.station.endAt, offset.value)
  const shift = item.location.shiftName ?? p.station.shiftName ?? 'sem turno'
  const leg = REQUESTED_LEG_LABELS[p.requestedLeg] ?? p.requestedLeg
  const mode = registrationModeLabel(p.registrationMode ?? p.requestChannel)
  return [
    {
      label: 'Plantão',
      value: `${period.weekday ? `${period.weekday}, ` : ''}${period.date} · ${period.range} · ${shift}`
    },
    { label: 'Unidade', value: item.location.healthUnitName },
    { label: 'Escala', value: item.location.scheduleName },
    { label: 'Médico', value: doctorFact(item) },
    { label: 'Perna / modalidade', value: `${leg} · ${mode}` },
    { label: 'Regra do prazo', value: ruleFact(p) },
    { label: 'Prazo', value: deadlineFact(p.sendDeadlineAt ?? item.sla?.deadlineAt ?? null) },
    { label: 'Pagamento', value: paymentFact(item) }
  ]
})

const history = ref<TimelineEntry[]>([])
const historyLoading = ref(false)
const historyError = ref<string | null>(null)
let requestSeq = 0

async function loadHistory(): Promise<void> {
  if (!props.item) return
  const seq = ++requestSeq
  historyLoading.value = true
  historyError.value = null
  try {
    const response = await Network.dsAttendance.getActivityRequestTimeline(props.item.entityId)
    if (seq !== requestSeq) return
    history.value = mapActivityRequestTimeline(response, offset.value)
  } catch (error) {
    if (seq !== requestSeq) return
    history.value = []
    historyError.value = getApiErrorMessage(
      error,
      'Não foi possível carregar o histórico da solicitação.'
    )
  } finally {
    if (seq === requestSeq) historyLoading.value = false
  }
}

watch(
  () => [props.modelValue, props.item?.entityId] as const,
  ([open, entityId]) => {
    if (open && entityId) void loadHistory()
  }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseSidebar, {
    "model-value": _ctx.modelValue,
    "background-color": "#FFFFFF",
    width: "560px",
    "onUpdate:modelValue": _cache[2] || (_cache[2] = (value) => emit('update:modelValue', value))
  }, {
    header: _withCtx(({ close }) => [
      (_ctx.item)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", null, [
                _createElementVNode("p", _hoisted_3, _toDisplayString(_ctx.item.actor?.name ?? 'Médico não informado'), 1),
                _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.item.actor?.crm ?? 'CRM não informado'), 1)
              ]),
              _createElementVNode("button", {
                type: "button",
                class: "text-sm text-neutral50",
                "data-testid": "activity-request-drawer-close",
                onClick: close
              }, " Fechar ", 8, _hoisted_5)
            ]),
            _createElementVNode("p", _hoisted_6, [
              _createTextVNode(_toDisplayString(_ctx.item.title), 1),
              (_ctx.item.subtitle)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createTextVNode(" · " + _toDisplayString(_ctx.item.subtitle), 1)
                  ], 64))
                : _createCommentVNode("", true)
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    default: _withCtx(() => [
      (_ctx.item)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createElementVNode("section", _hoisted_8, [
              _hoisted_9,
              _createElementVNode("div", _hoisted_10, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(strip.value, (point) => {
                  return (_openBlock(), _createElementBlock("span", {
                    key: point.key,
                    class: "absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-primary30",
                    style: _normalizeStyle({ left: `${point.pct}%` }),
                    title: `${point.label} · ${_unref(formatDateTimeInStation)(point.at, offset.value)}`
                  }, null, 12, _hoisted_11))
                }), 128))
              ]),
              _createElementVNode("ul", _hoisted_12, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(strip.value, (point) => {
                  return (_openBlock(), _createElementBlock("li", {
                    key: point.key
                  }, _toDisplayString(point.label) + ": " + _toDisplayString(_unref(formatDateTimeInStation)(point.at, offset.value)), 1))
                }), 128))
              ])
            ]),
            _createElementVNode("section", null, [
              _hoisted_13,
              _createElementVNode("table", _hoisted_14, [
                _createElementVNode("tbody", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(facts.value, (fact) => {
                    return (_openBlock(), _createElementBlock("tr", {
                      key: fact.label
                    }, [
                      _createElementVNode("th", _hoisted_15, _toDisplayString(fact.label), 1),
                      _createElementVNode("td", _hoisted_16, _toDisplayString(fact.value), 1)
                    ]))
                  }), 128))
                ])
              ])
            ]),
            _createElementVNode("section", null, [
              _hoisted_17,
              (historyLoading.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_18, "Carregando histórico…"))
                : (historyError.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                      _createElementVNode("span", null, _toDisplayString(historyError.value), 1),
                      _createElementVNode("button", {
                        type: "button",
                        class: "font-bold underline",
                        "data-testid": "activity-request-history-retry",
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (loadHistory()))
                      }, " Tentar novamente ")
                    ]))
                  : (_openBlock(), _createElementBlock("ol", _hoisted_20, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(history.value, (entry) => {
                        return (_openBlock(), _createElementBlock("li", {
                          key: entry.key,
                          class: _normalizeClass(["mb-2 border-l-4 pl-3", HISTORY_BORDER[entry.tone]])
                        }, [
                          _createElementVNode("p", _hoisted_21, _toDisplayString(entry.atLabel), 1),
                          _createElementVNode("p", _hoisted_22, _toDisplayString(entry.title), 1),
                          (entry.subtitle)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_23, _toDisplayString(entry.subtitle), 1))
                            : _createCommentVNode("", true)
                        ], 2))
                      }), 128)),
                      (!history.value.length)
                        ? (_openBlock(), _createElementBlock("li", _hoisted_24, "Sem eventos registrados."))
                        : _createCommentVNode("", true)
                    ]))
            ]),
            _createElementVNode("div", _hoisted_25, [
              _createVNode(OutlinedButton, {
                text: "Enviar registro",
                width: "160px",
                height: "32px",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('sendRecord', _ctx.item.entityId)))
              })
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["model-value"]))
}
}

})